import React, {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import InvoiceFilters from './InvoiceFilters';
import CheckboxWithIcon from '../../../components/Checkbox/CheckboxWithIcon';
import {useTranslation} from 'react-i18next';
import DataGrid from '../../../components/DataGrid/DataGrid';
import {generateColumns, InvoiceTableRow, useStyles,} from './InvoiceList.utils';
import {actions} from '../../../store';
import {convertUserLocalDateToUtc, convertUserLocalTimeToUtc,} from '../../../utils/dateWithTimezoneConversion';
import dayjs from '../../../services/customDayJs';
import {invoiceEntityTransformer} from '../../../utils/transformers';
import {PageChangeEvent, PaginationMode,} from '../../../components/DataGrid/types';
import {InvoiceFiltersType, statusOptions} from './InvoiceFilters.utils';
import {showToast} from "../../../utils/showToast";
import {useInvoiceData} from "../../../hooks/useInvoiceData";

const InvoiceList = (): ReactElement => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = useStyles();

    const [items, setItems] = useState<{
        data: InvoiceTableRow[];
        total: number;
    }>({data: [], total: 0});

    const {invoiceList, timezoneOffset, userDateTimeFormat, userDateFormat, currency, isLoading} = useInvoiceData()

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isDownloadingGoingOn, setIsDownloadingGoingOn] = useState<boolean[]>(
        [],
    );

    const [filters, setFilters] = useState<InvoiceFiltersType>({
        invoiceNumber: '',
        statusFilter: statusOptions[0],
        afterFilter: convertUserLocalTimeToUtc(
            dayjs().subtract(6, 'months').format(),
            timezoneOffset,
        ),
        beforeFilter: '',
        coveringFilter: '',
        showVoid: false,
    });

    const handlePageChange = (param: PageChangeEvent) => {
        setPage(param.page);
    };

    const handlePageSizeChange = (param: PageChangeEvent) => {
        setPageSize(param.pageSize);
        setPage(0);
    };
    const toggleTooltipVisibility = (index: number) => {
        const newDownloading = [...isDownloadingGoingOn];
        newDownloading[index] = false;
        setIsDownloadingGoingOn(newDownloading);
    };

    const downloadFile = (invoice_number: number, index: number) => {
        const newDownloading = [...isDownloadingGoingOn];
        newDownloading[index] = true;
        setIsDownloadingGoingOn(newDownloading);
        showToast(t<string>('common:downloadWillStartShortly'));
        dispatch(
            actions.getInvoiceSelectedFile.request({
                invoice_number: invoice_number,
                get_pdf: '1',
                fileName: t('screens:billing.invoiceName', {
                    number: invoice_number,
                }),
                callback: () => toggleTooltipVisibility(index),
            }),
        );
    };

    const columns = useMemo(() => {
        return generateColumns(
            t,
            currency,
            timezoneOffset,
            downloadFile,
            userDateTimeFormat,
        );
    }, [isLoading, timezoneOffset, userDateTimeFormat, currency]);

    const fetchData = useCallback((cache = 1500) => {

        const cacheLey = 'loadInvoiceInProgress'
        const sessionStateInProgress = cache > 0 ? window.localStorage.getItem(cacheLey) === 'true' : false

        if (!sessionStateInProgress) {
            window.localStorage.setItem(cacheLey, 'true')
            dispatch(
                actions.getInvoicesListAction.request({
                    issued_after: filters.afterFilter
                        ? convertUserLocalDateToUtc(
                            filters.afterFilter,
                            timezoneOffset,
                        )
                        : undefined,
                    issued_before: filters.beforeFilter
                        ? convertUserLocalDateToUtc(
                            filters.beforeFilter,
                            timezoneOffset,
                        )
                        : undefined,
                    covers_date: filters.coveringFilter
                        ? convertUserLocalTimeToUtc(
                            filters.coveringFilter,
                            timezoneOffset,
                        )
                        : undefined,
                    status: filters.statusFilter,
                    hide_void: !filters.showVoid ? 1 : 0,
                    invoiceNumber: filters.invoiceNumber,
                    limit: pageSize,
                    offset: page * pageSize,
                    callback: () => {
                        setTimeout(() => {
                                window.localStorage.setItem(cacheLey, 'false')
                            }, cache
                        )
                    }
                }),
            );
        }


    }, [filters, pageSize, page]);

    useEffect(() => {
        fetchData();
    }, [filters, pageSize, page]);

    useEffect(() => {
        if (invoiceList) {
            const {items, total} = invoiceList;
            setItems({
                total: total || 0,
                data: items.map(invoiceEntityTransformer) as InvoiceTableRow[],
            });
        }
    }, [invoiceList]);

    return (
        <>
            <div style={{maxWidth: 1140, minWidth: 1002}}>
                <InvoiceFilters
                    initData={filters}
                    onClick={() => fetchData(0)}
                    onChange={(values: InvoiceFiltersType) => setFilters(values)}
                    userDateTimeFormat={userDateTimeFormat}
                    userDateFormat={userDateFormat}
                />
            </div>
            <div style={{maxWidth: 1040, minWidth: 1002}}>

                <CheckboxWithIcon
                    dataQa={'show-void-invoice-checkbox'}
                    label={t('screens:billing.invoicesCheckboxLabel')}
                    tooltipText={t('tooltips:billing.invoicesCheckbox')}
                    containerStyle={classes.checkbox}
                    onChange={() =>
                        setFilters((prevState) => ({
                            ...prevState,
                            showVoid: !prevState.showVoid,
                        }))
                    }
                    checked={filters.showVoid}
                    value={filters.showVoid}
                    skipPermission
                />
                <DataGrid<InvoiceTableRow>
                    data={items.data}
                    columns={columns}
                    rowCount={items.total}
                    loading={isLoading}
                    classes={{
                        header: classes.header,
                        tableContainer: classes.tableContent,
                    }}
                    paginationMode={PaginationMode.Server}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    initialPageSize={10}
                    dataQa={'invoice-table'}
                />
            </div>
        </>

    );
};

export default InvoiceList;
